import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Grid, Typography, TextField, Switch as MuiSwitch, Button, Box, Autocomplete, MenuItem,
  FormControl,
} from '@mui/material';
import { debounce } from 'lodash';
import { IApplication, IDropdownApplications } from '../../../models/smp/application';
import { SMPAPI } from '../../../../src/fetch/smp';
import { ToastV3, HyperlinkField } from '../components';
import { useToastContext } from '../../../hooks';
import { useSmpLoading } from '../context';
import { ISmpBundle } from '../../../../src/models/smp-bundles';
import { SmpBundlesAPI } from '../../../../src/fetch/smp-bundles';
import { useScrollToTop } from './include/hooks';

interface EmployeeOption {
  id: number;
  name: string;
}

interface Props {
  application: IApplication;
}

export const ApplicationDetails: React.FC<Props> = ({ application }) => {
  useScrollToTop();
  const { showToast } = useToastContext();
  const { setLoading } = useSmpLoading();

  // Local UI state
  const [appData, setAppData] = useState<IApplication>(application);
  const [originalData, setOriginalData] = useState<IApplication>(application);
  const [editedFields, setEditedFields] = useState<Partial<Record<keyof IApplication, boolean>>>({});
  const [showToastBar, setShowToastBar] = useState(false);
  const [applicationNote, setApplicationNote] = useState('');
  const [originalNote, setOriginalNote] = useState('');
  const [vendorName, setVendorName] = useState(application.vendorName ?? '');
  const [sharepointLink, setSharepointLink] = useState(application.SharepointLink ?? '');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [loadingLogo, setLoadingLogo] = useState<boolean>(false);
  const [previewLogo, setPreviewLogo] = useState<string | null>(null);
  const [applicationTypes, setApplicationTypes] = useState<IDropdownApplications[]>([]);
  const [categories, setCategories] = useState<IDropdownApplications[]>([]);
  const [statusOptions, setStatusOptions] = useState<{ value: string; label: string }[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [ownerOptions, setOwnerOptions] = useState<EmployeeOption[]>([]);
  const [createdByOptions, setCreatedByOptions] = useState<EmployeeOption[]>([]);
  const [bundles, setBundles] = useState<ISmpBundle[]>([]);


  // Stable callbacks for loading parts of the page
  const loadLogo = useCallback(async () => {
    if (!application.LogoPath) return;
    setLoadingLogo(true);
    try {
      const fileId = SMPAPI.getFileIdFromLogoPath(application.LogoPath);
      if (fileId) {
        const url = await SMPAPI.getApplicationLogoUrl(fileId);
        setLogoUrl(url);
      }
    } catch (error) {
      console.error('Failed to load logo:', error);
    } finally {
      setLoadingLogo(false);
    }
  }, [application.LogoPath]);

  const loadNote = useCallback(async () => {
    if (!application.applicationID) return;
    try {
      const note = await SMPAPI.fetchApplicationNote(application.applicationID);
      setApplicationNote(note);
      setOriginalNote(note);
    } catch (error) {
      console.warn('Failed to fetch application note.', error);
      setApplicationNote('');
      setOriginalNote('');
    }
  }, [application.applicationID]);



  const loadEmployees = useCallback(async () => {
    try {
      const employees = await SMPAPI.fetchEmployeesLookup('');

      if (application.OwnerID) {
        const owner = employees.find(e => e.id === application.OwnerID);
        if (owner) setOwnerOptions([owner]);
      }

      if (application.CreatedBy) {
        const createdBy = employees.find(e => e.id === application.CreatedBy);
        if (createdBy) setCreatedByOptions([createdBy]);
      }
    } catch (error) {
      console.error('Failed to preload employees', error);
    }
  }, [application.OwnerID, application.CreatedBy]);


  const fetchOwnerOptions = async (q: string) => {
    try {
      setOwnerOptions(await SMPAPI.fetchEmployeesLookup(q));
    } catch (error) {
      console.error('Failed to fetch owners', error);
    }
  };

  const fetchCreatedByOptions = async (q: string) => {
    try {
      setCreatedByOptions(await SMPAPI.fetchEmployeesLookup(q));
    } catch (error) {
      console.error('Failed to fetch CreatedBy', error);
    }
  };

  const debouncedFetchOwner = useMemo(() => debounce(fetchOwnerOptions, 300), []);
  const debouncedFetchCreatedBy = useMemo(() => debounce(fetchCreatedByOptions, 300), []);


  // Initialize page data once per application change
  useEffect(() => {
    const initializePage = async () => {
      setLoading(true);
      setIsInitialized(false);
      try {
        const [types, cats, statuses, bundlesData] = await Promise.all([
          SMPAPI.fetchApplicationTypes(),
          SMPAPI.fetchCategories(),
          SMPAPI.fetchApplicationStatuses(),
          SmpBundlesAPI.getAll(),
        ]);
        setApplicationTypes(types);
        setCategories(cats);
        setStatusOptions(statuses);
        setBundles(bundlesData);

        setAppData(application);
        setOriginalData(application);
        setEditedFields({});
        setVendorName(application.vendorName ?? '');
        setSharepointLink(application.SharepointLink ?? '');
        setPreviewLogo(null);
        setLogoUrl(null);

        await Promise.all([loadLogo(), loadNote(), loadEmployees()]);
      } catch (error) {
        console.error('Page initialization failed:', error);
      } finally {
        setIsInitialized(true);
        setLoading(false);
      }
    };
    initializePage();
  }, [application, loadLogo, loadNote, loadEmployees, setLoading]);

  // Show save/cancel toast when edits are made
  useEffect(() => {
    if (!isInitialized) return;
    const hasEdits =
      Object.values(editedFields).some(Boolean) ||
      vendorName !== (originalData.vendorName ?? '') ||
      sharepointLink !== (originalData.SharepointLink ?? '') ||
      applicationNote.trim() !== originalNote.trim();
    setShowToastBar(hasEdits);
  }, [isInitialized, editedFields, vendorName, sharepointLink, applicationNote, originalNote, originalData.vendorName, originalData.SharepointLink]);

  // Handlers
  const handleChange = <K extends keyof IApplication>(field: K, value: IApplication[K]) => {
    setAppData(prev => ({ ...prev, [field]: value }));
    setEditedFields(prev => ({ ...prev, [field]: true }));
  };

  const handleGlobalCancel = () => {
    setAppData(originalData);
    setEditedFields({});
    setVendorName(originalData.vendorName ?? '');
    setSharepointLink(originalData.SharepointLink ?? '');
    setApplicationNote(originalNote);
    setPreviewLogo(null);
    setShowToastBar(false);
  };

  const handleGlobalSave = async () => {
    try {
      let logoPath = appData.LogoPath;
      if (appData.file) {
        logoPath = await SMPAPI.uploadApplicationLogo(appData.file);
      }
      let vendorID = appData.VendorID;
      if (vendorName.trim()) {
        const resp = await SMPAPI.saveVendor({ name: vendorName.trim() });
        vendorID = resp.vendorID;
      }
      if (applicationNote.trim() !== originalNote.trim()) {
        await SMPAPI.saveApplicationNote({ applicationID: appData.applicationID!, noteText: applicationNote.trim() });
        setOriginalNote(applicationNote.trim());
      }
      const updated = {
        ...appData,
        VendorID: vendorID,
        SharepointLink: sharepointLink,
        LogoPath: logoPath,
        file: undefined,
        assignedBundleIds: appData.assignedBundleIds,
      };
      await SMPAPI.upsertApplication(updated);
      const refreshed = await SMPAPI.getApplication(application.applicationID!);
      setAppData(refreshed);
      setOriginalData(refreshed);
      const fid = SMPAPI.getFileIdFromLogoPath(refreshed.LogoPath);
      if (fid) {
        setLogoUrl(await SMPAPI.getApplicationLogoUrl(fid));
      }
      setEditedFields({});
      setPreviewLogo(null);
      setShowToastBar(false);
      showToast('success', 'Application saved');
    } catch (error) {
      console.error('Update failed', error);
      showToast('error', 'Failed to update application');
    }
  };



  const toggles: { label: string; key: keyof IApplication }[] = [
    { label: 'Log In Credentials', key: 'LogInCredentialsRequired' },
    { label: 'Usage Report', key: 'UsageReportRequired' },
    { label: 'Admin Option', key: 'AdminOption' },
    { label: 'Single SignOn (SSO)', key: 'SingleSignOn' },
    { label: 'Invoice to Corp Payables', key: 'InvoiceToCorpPayables' },
    { label: 'FS Prepaid?', key: 'FSPrepaid' },
    { label: 'Publish in Catalog', key: 'PublishInCatalog' },
  ];


  const denseLabelStyle = {
    fontWeight: 500,
    fontSize: '0.8rem',
    color: '#333',
    marginBottom: 0.5
  };

  const denseInputStyle = {
    width: '100%',
    maxWidth: 300,
    borderRadius: '6px',
    '& .MuiInputBase-root': {
      height: 36,
      fontSize: '0.875rem'
    }
  };

  const denseSwitchStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    width: '100px',
    '& .MuiTypography-root': {
      fontSize: '0.75rem'
    }
  };

  const gridContainerStyle = {
    '& .MuiGrid-item': {
      paddingTop: '1.7px',
      paddingBottom: '1.7px'
    },
    margin: 0.5,
  };

  const smpHeaderStyles = {
    label: {
      fontWeight: 600,
      fontSize: '0.95rem',
      color: '#222',
    },
    input: {
      boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#ccc',
        borderWidth: '0px',
      },
    }
  };

  if (!isInitialized) return null;

  return (
    <>

      {/* Top Section - Dense */}
      <Grid container spacing={0.5} sx={{ ...gridContainerStyle, mb: 1 }}>
        <Grid item xs={false} sm={1} />
        <Grid item xs={12} sm={9}>
          <Grid container spacing={0.5}>
            {/* Mobile-only All Apps button */}
            <Grid item xs={12} sx={{ display: { xs: 'block', sm: 'none' }, mb: 1 }}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                href="/smp"
                sx={{
                  border: '2px double #00008B',
                  padding: '6px 12px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  color: '#000',
                }}
              >
                All Apps
              </Button>
            </Grid>

            {/* Name Row */}
            <Grid item xs={12} sm={3}>
              <Typography sx={{ ...denseLabelStyle, ...smpHeaderStyles.label }}>Name</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                fullWidth
                value={appData.name}
                onChange={e => handleChange('name', e.target.value)}
                sx={{ ...denseInputStyle, ...smpHeaderStyles.input }}
              />
            </Grid>
            {/* Desktop-only AllApps button */}
            <Grid item xs={12} sm={2} sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  size="small"
                  variant="outlined"
                  href="/smp"
                  sx={{
                    border: '2px double #00008B',
                    padding: '2px 8px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#000',
                  }}
                >
                  All Apps
                </Button>
              </Box>
            </Grid>

            {/* Logo Row */}
            <Grid item xs={12} sm={3}>
              <Typography sx={{ ...denseLabelStyle, ...smpHeaderStyles.label }}>Logo</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              {!logoUrl && !previewLogo ? (
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  sx={{
                    padding: '4px 8px',
                    fontSize: '0.75rem'
                  }}
                >
                  Upload Logo
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleChange('file', file);
                        const reader = new FileReader();
                        reader.onloadend = () => setPreviewLogo(reader.result as string);
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                </Button>
              ) : (
                <Box
                  sx={{
                    position: 'relative',
                    width: 'fit-content',
                    height: 50,
                    borderRadius: '6px',
                    overflow: 'hidden',
                    '&:hover .edit-overlay': {
                      opacity: 1,
                      transition: 'opacity 0.3s ease-in-out',
                    }
                  }}
                >
                  <img
                    src={previewLogo || logoUrl || ''}
                    alt="Application Logo"
                    style={{
                      height: 50,
                      borderRadius: 6,
                      objectFit: 'contain'
                    }}
                    onError={() => console.error('Logo failed to load')}
                  />
                  <Box
                    className="edit-overlay"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bottom: 0,
                      left: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      bgcolor: 'rgba(0,0,0,0.4)',
                      opacity: 0,
                      cursor: 'pointer',
                    }}
                    component="label"
                  >
                    {loadingLogo && (
                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'gray' }}>
                        Loading logo...
                      </Typography>
                    )}
                    <Typography sx={{ color: '#fff', fontSize: 20 }}>✎</Typography>
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleChange('file', file);
                          const reader = new FileReader();
                          reader.onloadend = () => setPreviewLogo(reader.result as string);
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Grid>
            <Grid item xs={false} sm={3} />

            {/* Description Row */}
            <Grid item xs={12} sm={3}>
              <Typography sx={{ ...denseLabelStyle, ...smpHeaderStyles.label }}>Description</Typography>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                multiline
                fullWidth
                size="small"
                minRows={2}
                placeholder="Description"
                value={appData.Description || ''}
                onChange={e => handleChange('Description', e.target.value)}
                sx={{
                  borderRadius: 1,
                  '& .MuiInputBase-root': { minHeight: '80px' },
                  ...smpHeaderStyles.input,
                }}
              />

            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={false} sm={2} />
      </Grid>

      {/* Mid Section */}
      <Grid container spacing={0.5} sx={gridContainerStyle}>
        <Grid item xs={false} sm={1} />
        <Grid item xs={12} sm={9}>
          <Box border={1} borderColor="#ccc" borderRadius={2} p={1.5} bgcolor="grey.100">
            <Grid container rowSpacing={1} columnSpacing={1}>
              {/* Type Row */}
              <Grid item xs={12} sm={3}>
                <Typography sx={denseLabelStyle}>Type</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  select
                  fullWidth
                  value={appData.TypeID || ''}
                  onChange={e => handleChange('TypeID', Number(e.target.value))}
                  sx={denseInputStyle}
                >
                  {applicationTypes.map(type => (
                    <MenuItem key={type.typeID} value={type.typeID} sx={{ fontSize: '0.8rem' }}>
                      {type.typeName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={false} sm={5} />

              <Grid item xs={12} sm={3}>
                <Typography sx={denseLabelStyle}>Website</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <HyperlinkField
                  label="Website"
                  value={appData.Website || ''}
                  onChange={(newVal) => handleChange('Website', newVal)}
                />
              </Grid>
              <Grid item xs={false} sm={5} />


              <Grid item xs={12} sm={3}>
                <Typography sx={denseLabelStyle}>Status</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={appData.Status || ''}
                  onChange={e => handleChange('Status', e.target.value)}
                  sx={denseInputStyle}
                >
                  {statusOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={false} sm={5} />


              {/* Category Row */}
              <Grid item xs={12} sm={3}>
                <Typography sx={denseLabelStyle}>Category</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  select
                  fullWidth
                  value={appData.CategoryID || ''}
                  onChange={e => handleChange('CategoryID', Number(e.target.value))}
                  sx={denseInputStyle}
                >
                  {categories.map(cat => (
                    <MenuItem key={cat.categoryID} value={cat.categoryID} sx={{ fontSize: '0.8rem' }}>
                      {cat.categoryName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={false} sm={5} />

              {/* Owner Row */}
              <Grid item xs={12} sm={3}>
                <Typography sx={denseLabelStyle}>Owner</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={ownerOptions}
                  getOptionLabel={(option) => option.name}
                  value={ownerOptions.find(emp => emp.id === appData.OwnerID) || null}
                  onInputChange={(event, value) => {
                    if (value.length >= 2) debouncedFetchOwner(value);
                  }}
                  onChange={(event, newValue) => handleChange('OwnerID', newValue?.id ?? undefined)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search owner"
                      sx={denseInputStyle}
                    />
                  )}
                />

              </Grid>
              <Grid item xs={false} sm={5} />


              {/* License Added By */}
              <Grid item xs={12} sm={3}>
                <Typography sx={denseLabelStyle}>License added By</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={createdByOptions}
                  getOptionLabel={(option) => option.name}
                  value={createdByOptions.find(emp => emp.id === appData.CreatedBy) || null}
                  onInputChange={(event, value) => {
                    if (value.length >= 2) debouncedFetchCreatedBy(value);
                  }}
                  onChange={(event, newValue) => handleChange('CreatedBy', newValue ? newValue.id : null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search employee"
                      sx={denseInputStyle}
                    />
                  )}
                />

              </Grid>
              <Grid item xs={false} sm={5} />



              {/* Toggles */}
              {toggles.map(({ label, key }) => {
                const checked = !!appData[key];
                return (
                  <React.Fragment key={key}>
                    <Grid item xs={12} sm={3}>
                      <Typography sx={denseLabelStyle}>{label}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={denseSwitchStyle}>
                        <Typography variant="body2" width={20}>
                          {checked ? 'Yes' : 'No'}
                        </Typography>
                        <MuiSwitch
                          size="small"
                          checked={checked}
                          onChange={e => handleChange(key, e.target.checked)}
                          sx={{
                            padding: 0.5,
                            '& .MuiSwitch-thumb': { backgroundColor: checked ? 'green' : undefined },
                            '& .MuiSwitch-track': { backgroundColor: checked ? 'rgba(0, 128, 0, 0.3)' : undefined },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={false} sm={5} />

                    {key === 'LogInCredentialsRequired' && (
                      <>
                        <Grid item xs={12} sm={3}>
                          <Typography sx={denseLabelStyle}>Vendor</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            size="small"
                            fullWidth
                            value={vendorName}
                            onChange={(e) => setVendorName(e.target.value)}
                            sx={denseInputStyle}
                            placeholder="Vendor Name"
                          />
                        </Grid>
                        <Grid item xs={false} sm={5} />

                        <Grid item xs={12} sm={3}>
                          <Typography sx={denseLabelStyle}>Document(s) Link</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <HyperlinkField
                            label="Document(s) Link"
                            value={sharepointLink}
                            onChange={(newVal) => setSharepointLink(newVal)}
                          />
                        </Grid>

                        <Grid item xs={false} sm={5} />
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={false} sm={2} />
      </Grid>


      <Grid container spacing={0.5} sx={gridContainerStyle}>
        <Grid item xs={false} sm={1} />
        <Grid item xs={12} sm={9}>
          <Box border={1} borderColor="#ccc" borderRadius={2} p={1.5} bgcolor="grey.100">
            {/*  inner Grid container for row layout */}
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs={12} sm={3}>
                <Typography sx={denseLabelStyle}>Assign to Bundle(s)</Typography>
              </Grid>
              <Grid item xs={12} sm={9}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Autocomplete
                    multiple
                    id="assignedBundles"
                    size="small"
                    options={bundles.filter(b => !appData.assignedBundleIds?.includes(b.smpBundleId))}
                    value={bundles.filter(b => appData.assignedBundleIds?.includes(b.smpBundleId))}
                    onChange={(event, newValue) =>
                      handleChange('assignedBundleIds', newValue.map(b => b.smpBundleId))
                    }
                    getOptionLabel={option => option.name}
                    isOptionEqualToValue={(option, value) => option.smpBundleId === value.smpBundleId}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder="Add Bundles"
                        variant="outlined"
                        size="small"
                      />
                    )}
                  />
                </FormControl>

              </Grid>
            </Grid>
            {/* End inner grid */}
          </Box>
        </Grid>
        <Grid item xs={false} sm={2} />
      </Grid>

      {/* Application Note */}
      <Grid container spacing={0.5} sx={gridContainerStyle} mt={1}>
        <Grid item xs={false} sm={1} />
        <Grid item xs={12} sm={9}>
          <Typography sx={{ ...denseLabelStyle, mb: 0.5 }}>Note</Typography>
          <TextField
            multiline
            minRows={3}
            fullWidth
            placeholder="Application-level notes or remarks..."
            value={applicationNote}
            onChange={e => setApplicationNote(e.target.value)}
            sx={{
              borderRadius: 2,
              '& .MuiInputBase-root': { minHeight: '90px' }
            }}
          />
        </Grid>
        <Grid item xs={false} sm={2} />
      </Grid>


      {showToastBar && (
        <ToastV3
          onCancel={handleGlobalCancel}
          onSave={handleGlobalSave}
          onClose={() => setShowToastBar(false)}
          showReset={false}
          type="warning"
        />
      )}
    </>
  );
};